import { useAuth } from '@components/auth/AuthContext';

/**
 * Hook for checking user capabilities and roles
 * Provides convenient methods for permission-based UI rendering
 */
export const useCapabilities = () => {
  const { user } = useAuth();

  /**
   * Check if user has a specific capability
   */
  const hasCapability = (capability: string): boolean => {
    if (!user?.capabilities) return false;
    return user.capabilities.includes(capability);
  };

  /**
   * Check if user has any of the specified capabilities
   */
  const hasAnyCapability = (capabilities: string[]): boolean => {
    if (!user?.capabilities) return false;
    return capabilities.some(capability => user.capabilities!.includes(capability));
  };

  /**
   * Check if user has all of the specified capabilities
   */
  const hasAllCapabilities = (capabilities: string[]): boolean => {
    if (!user?.capabilities) return false;
    return capabilities.every(capability => user.capabilities!.includes(capability));
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    if (!user?.roles) return false;
    return user.roles.includes(role);
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: string[]): boolean => {
    if (!user?.roles) return false;
    return roles.some(role => user.roles!.includes(role));
  };

  /**
   * Check if user has all of the specified roles
   */
  const hasAllRoles = (roles: string[]): boolean => {
    if (!user?.roles) return false;
    return roles.every(role => user.roles!.includes(role));
  };

  /**
   * Get user's capabilities array
   */
  const getCapabilities = (): string[] => {
    return user?.capabilities || [];
  };

  /**
   * Get user's roles array
   */
  const getRoles = (): string[] => {
    return user?.roles || [];
  };

  /**
   * Check if user can perform offer-related actions
   */
  const canViewOffers = (): boolean => hasCapability('offers.view');
  const canCreateOffers = (): boolean => hasCapability('offers.create');
  const canUpdateOffers = (): boolean => hasCapability('offers.update');
  const canDeleteOffers = (): boolean => hasCapability('offers.delete');

  /**
   * Check if user can perform company settings actions
   */
  const canViewCompanySettings = (): boolean => hasCapability('company_settings.view');
  const canUpdateCompanySettings = (): boolean => hasCapability('company_settings.update');
  const canManageUsers = (): boolean => hasCapability('company_settings.manage_users');
  const canManageSSO = (): boolean => hasCapability('company_settings.manage_sso');

  /**
   * Check if user can perform valuation actions
   */
  const canViewValuations = (): boolean => hasCapability('valuations.view');
  const canUpdateValuations = (): boolean => hasCapability('valuations.update');

  /**
   * Check if user can perform other actions
   */
  const canSubmitFeedback = (): boolean => hasCapability('feedback.submit');
  const canViewWhatsNew = (): boolean => hasCapability('whatsnew.view');

  /**
   * Audit capabilities
   */
  const canViewAuditEvents = (): boolean => hasCapability('audit.view');
  const canExportAuditEvents = (): boolean => hasCapability('audit.export');

  return {
    // Core capability checking
    hasCapability,
    hasAnyCapability,
    hasAllCapabilities,

    // Role checking
    hasRole,
    hasAnyRole,
    hasAllRoles,

    // Data access
    getCapabilities,
    getRoles,

    // Specific capability checks
    canViewOffers,
    canCreateOffers,
    canUpdateOffers,
    canDeleteOffers,
    canViewCompanySettings,
    canUpdateCompanySettings,
    canManageUsers,
    canManageSSO,
    canViewValuations,
    canUpdateValuations,
    canSubmitFeedback,
    canViewWhatsNew,
    canViewAuditEvents,
    canExportAuditEvents,
  };
};
